import { Button } from '@/components/ui/button';
import useStore from '@/lib/store';
import { Download } from 'lucide-react';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { useMonitoringStore } from '../../../store/useMonitoringStore';
import { NavigateTitle } from '../../navigate-title/NavigateTitle';
import { TableMonitoringLoading } from '../TableLoading';
import { TableRepublic } from './table/TableRepublic';
import { exportMonitoringToExcel } from '../../../utils/exportMonitoringToExcel';
import { axiosInstance } from '@/common/axios/axios.instance';
import { ENDPOINT_ROOT } from '@/common/constants';

export const MonitoringRepublic: FC = () => {
  const [searchParams] = useSearchParams();
  const plant_id = searchParams.get('plant_id');
  const year = searchParams.get('year');
  const {
    cottonData,
    dataRepublic,
    dataRepublicCachedAt,
    cottonLoading,
    republicLoading,
    fetchCottonRepublic,
    fetchMonitoringRepublic,
  } = useMonitoringStore();
  const { user } = useStore();
  useEffect(() => {
    if (!user?.region_id) {
      if (plant_id && year) {
        fetchCottonRepublic(plant_id, year);
        fetchMonitoringRepublic(plant_id, year);
      }
    }
  }, [plant_id, year]);
  const { t } = useTranslation();

  const [seedReportLoading, setSeedReportLoading] = useState(false);

  const handleExport = async () => {
    if (dataRepublic.length > 0) {
      await exportMonitoringToExcel(dataRepublic, cottonData, plant_id || '2', t);
    }
  };

  const handleSeedReport = async () => {
    try {
      setSeedReportLoading(true);
      const res = await axiosInstance.get(
        `${ENDPOINT_ROOT}/api/projects/statistics/all-regions-districts/excel`,
        {
          params: { plant_id: 2 },
          responseType: 'blob',
        },
      );
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'Chigit_ekish_hisoboti.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error('Seed report download error:', err);
    } finally {
      setSeedReportLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-8 h-full">
      <div className="flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <NavigateTitle />
            {dataRepublicCachedAt && (
              <p className="text-sm text-gray-500 font-medium">
                {t('data.actual.on', {
                  date: format(
                    new Date(dataRepublicCachedAt),
                    'yyyy-MM-dd HH:mm',
                  ),
                })}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            {!user?.region_id && plant_id === '2' && (
              <Button
                onClick={handleSeedReport}
                isLoading={seedReportLoading}
                icon={<Download className="size-4" />}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white"
              >
                Chigit ekish hisoboti
              </Button>
            )}
            {!user?.region_id && dataRepublic.length > 0 && (
              <Button
                onClick={handleExport}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white"
              >
                <Download className="size-4" />
                Excel
              </Button>
            )}
          </div>
        </div>
      </div>
      {user?.region_id ? (
        <div className="w-full flex-1 flex justify-center items-center">
          <h2 className="text-2xl">{t('not.available.page')}...</h2>
        </div>
      ) : republicLoading || cottonLoading ? (
        <TableMonitoringLoading />
      ) : (
        <div className="flex-1 overflow-hidden">
          <TableRepublic dataRegions={dataRepublic} cottonData={cottonData} />
        </div>
      )}
    </div>
  );
};
